import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Space, Typography, Spin, Result, Button } from 'antd';
import { ArrowLeftOutlined, PrinterOutlined, ReloadOutlined } from '@ant-design/icons';
import { workOrdersApi } from './workOrdersApi';

// ─────────────────────────────────────────────────────────────────
// Glass-i — Work Order piece QR stickers viewer (Stage WO-1A).
//
// Mirrors the GRN receipt viewer: the backend renders the stickers
// synchronously (GET /work-orders/:id/stickers → HTML) via the generic
// Jasper engine, and the HTML drops straight into a sandboxed iframe.
// The template is 100 × 50 mm, one label per page; its embedded @page CSS
// prints one QR label per physical page on a thermal label printer.
// ─────────────────────────────────────────────────────────────────

function errMessage(err) {
  return (err && err.response && typeof err.response.data === 'string' && err.response.data.length < 300 && err.response.data)
    || (err && err.message) || 'Could not load the stickers.';
}

export default function WorkOrderStickersPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [html, setHtml] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const wrapRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setHtml(null);
    try {
      setHtml(await workOrdersApi.stickers(id));
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handlePrint = () => {
    const iframe = wrapRef.current && wrapRef.current.querySelector('iframe');
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }
  };

  return (
    <Card
      title={
        <Space direction="vertical" size={0}>
          <Typography.Text strong>Piece Stickers</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            100 × 50 mm QR labels — one per piece
          </Typography.Text>
        </Space>
      }
      extra={
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>Back</Button>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Reload</Button>
          <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint} disabled={!html}>Print</Button>
        </Space>
      }
      styles={{ body: { padding: 0 } }}
    >
      <div ref={wrapRef} style={{ height: 800, borderTop: '1px solid #f0f0f0' }}>
        {loading ? (
          <div style={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spin tip="Rendering stickers…" />
          </div>
        ) : error ? (
          <div style={{ padding: 24 }}>
            <Result
              status="error"
              title="Could not render stickers"
              subTitle={error}
              extra={
                <Space>
                  <Button onClick={() => navigate(-1)}>Back</Button>
                  <Button type="primary" onClick={load}>Retry</Button>
                </Space>
              }
            />
          </div>
        ) : (
          <div style={{ width: '100%', height: '100%', overflow: 'auto', background: '#f0f2f5' }}>
            <iframe
              title="Work Order Stickers"
              srcDoc={html || ''}
              sandbox="allow-same-origin allow-modals"
              style={{ width: '100%', height: '100%', border: 'none', background: '#f0f2f5' }}
            />
          </div>
        )}
      </div>
    </Card>
  );
}
